/**
 * Cognito configuration, inlined by Vite at BUILD time from `VITE_COGNITO_*`.
 * These three values are PUBLIC, non-secret identifiers (the SPA app client has
 * NO secret) — safe to ship in the static bundle. The deploy workflow sets them
 * for environments backed by a real user pool; unset elsewhere, where the app
 * falls back to the dev API / fixture seam.
 *
 * The AWS region is derived from the user-pool id (`<region>_<poolsuffix>`) so
 * there is one source of truth and no way to point the JWKS at the wrong region.
 */
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID?.trim();
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID?.trim();

/** True only when BOTH pool id and client id were configured at build time. */
export const COGNITO_CONFIGURED: boolean = Boolean(userPoolId && clientId);

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  region: string;
}

/**
 * The validated config, or null when Cognito is not configured. Throws only if
 * a pool id is present but malformed — a broken deploy should fail loudly, not
 * silently drop to an insecure path.
 */
export function getCognitoConfig(): CognitoConfig | null {
  if (!userPoolId || !clientId) {
    return null;
  }
  const region = userPoolId.split('_')[0];
  if (!region) {
    throw new Error(`VITE_COGNITO_USER_POOL_ID is malformed: "${userPoolId}"`);
  }
  return { userPoolId, clientId, region };
}
